import { Preferences } from "@capacitor/preferences";
import { PushNotifications } from "@capacitor/push-notifications";
import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { updateNotificationPreferences } from "../api/settings.js";
import { getActiveOrders } from "@/store/orders/action.js";

// Get storage key for current user
const getStorageKey = (userId) => {
  return `app_notifications_${userId || "anonymous"}`;
};

// Track pending server sync operations to prevent race conditions
const pendingSyncOperations = new Map();

// Store the current userId for the registration listener
let currentUserId = null;

// Set the current user ID for notification operations
export const setCurrentUserId = (userId) => {
  currentUserId = userId;
  localStorage.setItem("userId", userId);
};

// Sync notification settings to server
const syncToServer = async (
  userId,
  settings,
  pushToken = null,
  sound = null,
  soundOrderUpdated = null,
  soundOrderCanceled = null
) => {
  const operationKey = `${userId}_sync`;

  // If there's already a pending sync for this user, wait for it to complete
  if (pendingSyncOperations.has(operationKey)) {
    return pendingSyncOperations.get(operationKey);
  }

  // Create a new sync operation
  const syncPromise = (async () => {
    try {
      const token = pushToken || localStorage.getItem("push_token");
      if (!token) {
        return;
      }
      // Get app and device information
      const [appInfo, deviceInfo] = await Promise.all([
        App.getInfo().catch(() => ({ id: "unknown", name: "unknown" })),
        Promise.resolve({ platform: Capacitor.getPlatform() }),
      ]);

      // Convert settings to array format - only include location-based settings
      const notificationSettingsArray = Object.entries(settings)
        .filter(([key, value]) => key !== "sound" && key !== "soundOrderUpdated" && key !== "soundOrderCanceled") // Exclude sounds from location settings
        .map(([locationId, setting]) => ({
          vendor: locationId,
          store_id: setting.store_id || null,
          push: setting.notification,
        }));

      // Get sound preferences, with fallbacks
      sound = sound || settings.sound || "Gentle Ping.aiff";
      soundOrderUpdated = soundOrderUpdated || settings.soundOrderUpdated || settings.sound || "Gentle Ping.aiff";
      soundOrderCanceled = soundOrderCanceled || settings.soundOrderCanceled || settings.sound || "Gentle Ping.aiff";
      
      // Convert to MP3 format for Android
      if (deviceInfo.platform === "android") {
        const convertToMp3 = (soundFile) => {
          return "notif_" + soundFile.replace(".aiff", "").toLowerCase().replace(" ", "");
        };
        sound = convertToMp3(sound);
        soundOrderUpdated = convertToMp3(soundOrderUpdated);
        soundOrderCanceled = convertToMp3(soundOrderCanceled);
      }
      
      // Prepare the payload
      const payload = {
        user_id: userId,
        push_token: token,
        notification_settings: notificationSettingsArray,
        app_id: appInfo.id,
        app_name: appInfo.name,
        platform: deviceInfo.platform,
        order_sound: sound,
        order_updated_sound: soundOrderUpdated,
        order_canceled_sound: soundOrderCanceled,
      };

      // Call your API endpoint (you'll need to create this endpoint)
      await updateNotificationPreferences(payload);
    } catch (error) {
      throw error;
    } finally {
      // Remove from pending operations
      pendingSyncOperations.delete(operationKey);
    }
  })();

  // Store the promise to prevent race conditions
  pendingSyncOperations.set(operationKey, syncPromise);

  return syncPromise;
};

// Load notification settings from Capacitor Preferences
export const loadNotificationSettings = async (userId) => {
  try {
    const { value } = await Preferences.get({ key: getStorageKey(userId) });
    if (value) {
      const savedSettings = JSON.parse(value);
      return savedSettings;
    }
    return {};
  } catch (error) {
    return {};
  }
};

// Save notification settings to Capacitor Preferences and sync to server
export const saveNotificationSettings = async (userId, settings) => {
  try {
    // Save to local storage first
    await Preferences.set({
      key: getStorageKey(userId),
      value: JSON.stringify(settings),
    });

    // Sync to server (non-blocking)
    syncToServer(userId, settings).catch((error) => {});
  } catch (error) {
    throw error;
  }
};

// Initialize notification settings for a user with their merchants
export const initializeNotificationSettings = async (
  user,
  merchants,
  navigate,
  dispatch
) => {
  const userId = user.id;
  if (!userId || !merchants || merchants.length === 0) {
    return {};
  }

  try {
    // Set the current user ID for the registration listener
    setCurrentUserId(userId);

    // Load existing settings
    const savedSettings = await loadNotificationSettings(userId);

    // Start with saved settings or empty object
    let currentSettings = { ...savedSettings };
    let hasNewLocations = false;

    // Ensure all current locations have settings (default to true)
    merchants.forEach((merchant) => {
      merchant.locations?.forEach((location) => {
        if (!currentSettings[location.id] && user.type !== "saucey_admin") {
          currentSettings[location.id] = {
            notification: true, // Default to true
            store_id: location.store_id,
          };
          hasNewLocations = true;
        }
      });
    });

    // Register notifications (this will trigger token registration asynchronously)
    try {
      await registerNotifications(navigate, dispatch);
    } catch (error) {}

    // Save settings locally (but don't sync to server yet)
    // The sync will happen when the token is received via the registration listener
    if (hasNewLocations || Object.keys(savedSettings).length === 0) {
      await Preferences.set({
        key: getStorageKey(userId),
        value: JSON.stringify(currentSettings),
      });
    }

    return currentSettings;
  } catch (error) {
    return {};
  }
};

const addListeners = async (navigate, dispatch) => {
  await PushNotifications.addListener("registration", async (token) => {
    localStorage.setItem("push_token", token.value);

    // When we get a new token, sync current settings to server
    const userId = currentUserId || localStorage.getItem("userId");

    if (userId) {
      try {
        const settings = await loadNotificationSettings(userId);
        if (Object.keys(settings).length > 0) {
          // Extract sounds from settings if they exist
          const sound = settings.sound || "Gentle Ping.aiff";
          const soundOrderUpdated = settings.soundOrderUpdated || settings.sound || "Gentle Ping.aiff";
          const soundOrderCanceled = settings.soundOrderCanceled || settings.sound || "Gentle Ping.aiff";
          // Remove sounds from settings before syncing
          const { sound: _, soundOrderUpdated: __, soundOrderCanceled: ___, ...locationSettings } = settings;
          await syncToServer(userId, locationSettings, token.value, sound, soundOrderUpdated, soundOrderCanceled);
        }
      } catch (error) {}
    }
  });

  await PushNotifications.addListener("registrationError", (err) => {
    localStorage.removeItem("push_token");
  });

  await PushNotifications.addListener(
    "pushNotificationReceived",
    (notification) => {
      if (notification.data?.click_action?.screen === "active-order-modal" || notification.data?.screen === "active-order-modal") {
        try {
          dispatch(getActiveOrders(true));
        } catch (error) {}
      }
    }
  );

  await PushNotifications.addListener(
    "pushNotificationActionPerformed",
    (notification) => {
      if (!navigate) return;

    
      switch (notification?.notification?.data?.click_action?.screen || notification?.notification?.data?.screen) {
        case "active-order-modal":
          navigate({
            pathname: "orders",
            search: new URLSearchParams({
              id: notification?.notification?.data?.click_action?.id || notification?.notification?.data?.id,
            }).toString(),
          });
          break;
        default:
          break;
      }
    }
  );
};

export const registerNotifications = async (navigate, dispatch) => {
  let permStatus = await PushNotifications.checkPermissions();

  if (permStatus.receive === "prompt") {
    permStatus = await PushNotifications.requestPermissions();
  }

  if (permStatus.receive !== "granted") {
    throw new Error("User denied permissions!");
  }

  await PushNotifications.register();
  await addListeners(navigate, dispatch);
};

export const getDeliveredNotifications = async () => {
  const notificationList = await PushNotifications.getDeliveredNotifications();
  return notificationList;
};

// Manual sync function for when you need to ensure server is up to date
export const forceSyncToServer = async (
  userId,
  settings = null,
  pushToken = null,
  sound = null,
  soundOrderUpdated = null,
  soundOrderCanceled = null
) => {
  try {
    const currentSettings =
      settings || (await loadNotificationSettings(userId));
    await syncToServer(userId, currentSettings, pushToken, sound, soundOrderUpdated, soundOrderCanceled);
  } catch (error) {
    throw error;
  }
};

// Wipe notification settings from storage (developer only)
export const wipeNotificationSettings = async (userId) => {
  try {
    const storageKey = getStorageKey(userId);
    await Preferences.remove({ key: storageKey });
  } catch (error) {
    throw error;
  }
};

// Get user ID from Redux store (you'll need to pass this from components)
export const getCurrentUserId = () => {
  // This is a placeholder - you'll need to get this from your Redux store
  // or pass it as a parameter to functions that need it
  return localStorage.getItem("userId");
};
